/**
 * عکسِ سریال/پلاکِ اموال (v2.151.0).
 *
 * فشرده‌سازی دو مرحله دارد: اینجا (پیش از ارسال) عکسِ ۱۲ مگاپیکسلیِ گوشی به ضلعِ ۲۰۰۰px و JPEGِ ۰٫۹ کوچک می‌شود تا
 * روی اینترنتِ گوشی سریع برود؛ سرور (`asset_photo.py`) نسخهٔ نهایی را ۱۶۰۰px و ≤ ~۴۵۰KB می‌سازد و EXIF را پاک می‌کند.
 * عکس پشتِ ورود است ⇒ با کلاینتِ احرازشده به‌شکلِ blob خوانده می‌شود.
 */
import React, { useEffect, useRef, useState } from "react";
import { Button, Image, Space, Tooltip, Typography, Modal, Spin } from "antd";
import { CameraOutlined, DeleteOutlined, PictureOutlined } from "@ant-design/icons";
import api from "../api/client";

const { Text } = Typography;

/** پیش‌فشرده‌سازی در مرورگر؛ اگر نشد (مثلاً HEIC در کروم) همان فایل را برمی‌گرداند و سرور کار را تمام می‌کند */
export async function shrinkImage(file, maxSide = 2000, quality = 0.9) {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const r = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    if (r === 1 && file.size < 1.5 * 1024 * 1024) return file;
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * r); c.height = Math.round(bmp.height * r);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", quality));
    return blob && blob.size < file.size ? new File([blob], "photo.jpg", { type: "image/jpeg" }) : file;
  } catch { return file; }
}

export const fmtKB = (b) => (b ? `${Math.round(b / 1024).toLocaleString("fa-IR")} KB` : "");

/** blobِ عکسِ ذخیره‌شده */
export function useAssetPhotoUrl(assetId, enabled, thumb = true, version = "") {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!assetId || !enabled) { setUrl(null); return undefined; }
    let alive = true; let obj = null;
    api.get(`/api/v1/assets/${assetId}/photo`, { params: { thumb }, responseType: "blob" })
      .then((r) => { if (alive) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
      .catch(() => alive && setUrl(null));
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [assetId, enabled, thumb, version]);
  return url;
}

/** پیش‌نمایشِ بزرگ (عکسِ کامل) */
export function AssetPhotoPreview({ asset, onClose }) {
  const url = useAssetPhotoUrl(asset?.id, !!asset, false, asset?.updated_at);
  return (
    <Modal open={!!asset} onCancel={onClose} footer={null} width={720} destroyOnHidden
      title={asset ? `عکسِ ${asset.code} — ${asset.name}` : ""}>
      {url ? <img src={url} alt="" style={{ width: "100%", borderRadius: 8 }} />
        : <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>}
      {asset?.photo_bytes ? <Text type="secondary" style={{ fontSize: 12 }}>حجمِ ذخیره‌شده: {fmtKB(asset.photo_bytes)}</Text> : null}
    </Modal>
  );
}

/**
 * فیلدِ فرم: value = { file?: File, remove?: bool }؛ عکسِ فعلیِ سرور از `asset` خوانده می‌شود.
 */
export default function AssetPhotoField({ asset, value, onChange, disabled }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const serverUrl = useAssetPhotoUrl(asset?.id, !!asset?.has_photo && !value?.remove && !value?.file, true, asset?.updated_at);
  const [localUrl, setLocalUrl] = useState(null);
  useEffect(() => {
    if (!value?.file) { setLocalUrl(null); return undefined; }
    const u = URL.createObjectURL(value.file); setLocalUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [value?.file]);
  const url = localUrl || serverUrl;

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const small = await shrinkImage(f);
    setBusy(false);
    onChange?.({ file: small, origBytes: f.size });
  };

  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={pick} />
      <input ref={galRef} type="file" accept="image/*" hidden onChange={pick} />
      <div style={{ width: 72, height: 72, borderRadius: 8, overflow: "hidden", flex: "none",
        border: "1px dashed var(--border, #d9d9d9)", display: "flex", alignItems: "center", justifyContent: "center",
        background: "var(--bg-soft, #fafafa)" }}>
        {busy ? <Spin size="small" />
          : url ? <Image src={url} width={72} height={72} style={{ objectFit: "cover" }} preview={{ mask: "بزرگ" }} />
            : <CameraOutlined style={{ fontSize: 24, color: "#aaa" }} />}
      </div>
      <Space direction="vertical" size={4}>
        <Space wrap size={6}>
          <Button size="small" type="primary" ghost icon={<CameraOutlined />} disabled={disabled}
            onClick={() => camRef.current?.click()}>{url ? "عکسِ دوباره" : "عکس از سریال"}</Button>
          <Tooltip title="انتخاب از گالری">
            <Button size="small" icon={<PictureOutlined />} disabled={disabled} onClick={() => galRef.current?.click()} />
          </Tooltip>
          {url && (
            <Tooltip title="حذفِ عکس">
              <Button size="small" danger icon={<DeleteOutlined />} disabled={disabled}
                onClick={() => onChange?.(asset?.has_photo ? { remove: true } : {})} />
            </Tooltip>
          )}
        </Space>
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          {value?.file ? `آماده (${fmtKB(value.origBytes)} ⇐ پس از ذخیره ≈ کمتر از ۴۵۰ KB)`
            : value?.remove ? "با ذخیره حذف می‌شود"
              : asset?.has_photo ? `ذخیره‌شده: ${fmtKB(asset.photo_bytes)}` : "حجم خودکار بهینه می‌شود"}
        </Text>
      </Space>
    </div>
  );
}
